import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  ArrowLeft,
  Bot,
  Loader2,
  Save,
  Calendar,
  MessageSquare,
  CheckCircle2,
  AlertTriangle,
  Send,
  Sparkles,
  Clock,
  History,
  ChevronDown,
  ChevronUp,
  Zap,
} from "lucide-react";

const CANAIS_INFO = [
  {
    id: "whatsapp_manual",
    nome: "WhatsApp manual (gratuito)",
    descricao:
      "O sistema gera os links e abre o WhatsApp Web/App com mensagem pronta — você confirma o envio com 1 clique cada.",
    custo: "Grátis",
    badge: "Recomendado",
    fase: 2,
    ativo: true,
    automatico: false,
  },
  {
    id: "whatsapp_zapi",
    nome: "WhatsApp via Z-API",
    descricao:
      "Envio 100% automático via Z-API (provedor brasileiro popular). Cadastre Instance ID + Token uma vez e o sistema envia sozinho.",
    custo: "~R$50–200/mês",
    badge: "FASE 3 — automático",
    fase: 3,
    ativo: true,
    automatico: true,
  },
  {
    id: "whatsapp_evolution",
    nome: "WhatsApp via Evolution API",
    descricao:
      "Open source, hospedado por você ou em servidor próprio. Sem custo de mensagem.",
    custo: "Custo do servidor",
    badge: "FASE 3 — automático",
    fase: 3,
    ativo: true,
    automatico: true,
  },
  {
    id: "email_resend",
    nome: "E-mail via Resend",
    descricao:
      "Envio automático de e-mail (3.000/mês grátis). Reforço para a cobrança de WhatsApp.",
    custo: "Grátis até 3000/mês",
    badge: "FASE 3 — automático",
    fase: 3,
    ativo: true,
    automatico: true,
  },
];

export default function CobrancaConfig({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [historico, setHistorico] = useState(null);
  const [showCreds, setShowCreds] = useState({
    whatsapp_zapi: false,
    whatsapp_evolution: false,
    email_resend: false,
  });
  const [testing, setTesting] = useState({});
  const [testPhone, setTestPhone] = useState("");
  const [testEmail, setTestEmail] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [cfg, sch, hist] = await Promise.all([
        api.get("/cobranca/config"),
        api.get("/cobranca/scheduler-status").catch(() => ({ data: null })),
        api.get("/cobranca/historico").catch(() => ({ data: null })),
      ]);
      setConfig(cfg.data);
      setScheduler(sch.data);
      setHistorico(hist.data);
    } catch (e) {
      toast.error("Erro ao carregar configuração");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = (key, value) => setConfig((c) => ({ ...c, [key]: value }));

  const toggleCanal = (canalId) => {
    setConfig((c) => {
      const atual = c.canais || [];
      const novo = atual.includes(canalId)
        ? atual.filter((x) => x !== canalId)
        : [...atual, canalId];
      return { ...c, canais: novo };
    });
    if (canalId !== "whatsapp_manual") {
      setShowCreds((s) => ({ ...s, [canalId]: true }));
    }
  };

  const toggleDiaApos = (dia) => {
    setConfig((c) => {
      const atual = c.dias_apos_vencimento || [];
      const novo = atual.includes(dia)
        ? atual.filter((x) => x !== dia)
        : [...atual, dia].sort((a, b) => a - b);
      return { ...c, dias_apos_vencimento: novo };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/cobranca/config", config);
      toast.success("Configuração salva");
    } catch {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (kind) => {
    setTesting((t) => ({ ...t, [kind]: true }));
    try {
      // salva antes de testar
      await api.put("/cobranca/config", config);
      const url = `/cobranca/test-${kind}`;
      const payload =
        kind === "resend"
          ? { test_email: testEmail || null }
          : { test_phone: testPhone || null };
      const r = await api.post(url, payload);
      const data = r.data || {};
      if (data.ok || data.connected || data.configured) {
        const extra =
          data.connected !== undefined
            ? ` (conectado: ${data.connected ? "✅" : "❌ scan QR"})`
            : "";
        toast.success(`Conexão OK${extra}`);
      } else if (data.error) {
        toast.error(`Falha: ${data.error}`);
      } else {
        toast.success("Resposta recebida — verifique o console");
      }
      // eslint-disable-next-line no-console
      console.log(`Teste ${kind}:`, data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no teste");
    } finally {
      setTesting((t) => ({ ...t, [kind]: false }));
    }
  };

  const dispararAgora = async () => {
    if (!window.confirm("Disparar cobranças automáticas AGORA para todos os pendentes do dia? (Usa as credenciais configuradas)")) return;
    try {
      const r = await api.post("/cobranca/disparar-automatico");
      const d = r.data || {};
      if (d.skipped) {
        toast.warning(`Pulado: ${d.reason}`);
      } else {
        toast.success(`Enviados: ${d.enviados} | Falhas: ${d.falhas} | Pulados: ${d.pulados}`);
      }
      // recarregar histórico
      const hist = await api.get("/cobranca/historico");
      setHistorico(hist.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao disparar");
    }
  };

  if (loading || !config) {
    return (
      <Layout onLogout={onLogout}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
        </div>
      </Layout>
    );
  }

  const proximaExecucao = scheduler?.jobs?.[0]?.next_run_time;

  return (
    <Layout onLogout={onLogout}>
      <div className="max-w-3xl mx-auto space-y-6 pb-12">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            data-testid="btn-voltar-cobranca-config"
            className="text-gray-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <Button
            onClick={() => navigate("/cobranca/em-massa")}
            variant="outline"
            data-testid="btn-ir-cobranca-massa"
          >
            <Send className="w-4 h-4 mr-2" />
            Disparar manualmente
          </Button>
        </div>

        <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white">
                <Bot className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <Badge className="bg-emerald-600 mb-1">FASE 3 ATIVA</Badge>
                <h1
                  className="text-2xl font-bold mb-1"
                  style={{ fontFamily: "Space Grotesk" }}
                >
                  Cobrança Automática
                </h1>
                <p className="text-sm text-gray-600">
                  Configure credenciais Z-API/Evolution/Resend e ative o
                  envio diário automático às 09:00 (sem clique manual).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Toggle ON/OFF */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  Ativar cobrança automática
                </CardTitle>
                <CardDescription>
                  Quando ativada, o sistema aplica seu cronograma e canais.
                </CardDescription>
              </div>
              <Switch
                checked={config.enabled}
                onCheckedChange={(v) => update("enabled", v)}
                data-testid="switch-cobranca-enabled"
              />
            </div>
          </CardHeader>
        </Card>

        {/* Cron diário */}
        <Card className={config.auto_send_diario ? "border-emerald-300 bg-emerald-50/30" : ""}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-emerald-500" />
                  Envio diário automático (09:00)
                </CardTitle>
                <CardDescription>
                  Quando ativado, o sistema envia cobranças sozinho todo dia
                  às 09:00 (horário de SP) usando os canais automáticos
                  configurados — sem precisar clicar em nada.
                </CardDescription>
              </div>
              <Switch
                checked={config.auto_send_diario}
                onCheckedChange={(v) => update("auto_send_diario", v)}
                data-testid="switch-auto-send-diario"
              />
            </div>
          </CardHeader>
          {(config.auto_send_diario || proximaExecucao) && (
            <CardContent>
              {proximaExecucao && (
                <div className="text-xs text-gray-600 flex items-center gap-2 mb-3">
                  <Zap className="w-3 h-3 text-emerald-600" />
                  Próxima execução automática:{" "}
                  <strong>
                    {new Date(proximaExecucao).toLocaleString("pt-BR", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </strong>
                </div>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={dispararAgora}
                data-testid="btn-disparar-automatico-agora"
              >
                <Zap className="w-4 h-4 mr-2" />
                Testar disparo agora
              </Button>
            </CardContent>
          )}
        </Card>

        {/* Cronograma */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              Cronograma de cobranças
            </CardTitle>
            <CardDescription>
              Quando enviar lembretes em relação ao dia do vencimento.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-semibold">
                Lembrete antes do vencimento
              </Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  min="0"
                  max="30"
                  value={config.dias_lembrete_antes}
                  onChange={(e) =>
                    update("dias_lembrete_antes", parseInt(e.target.value) || 0)
                  }
                  className="w-24"
                  data-testid="input-dias-antes"
                />
                <span className="text-sm text-gray-600">
                  dias antes do vencimento (0 = desativa)
                </span>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold">
                Reenvios após o vencimento
              </Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {[1, 3, 5, 7, 10, 15].map((dia) => {
                  const ativo = (config.dias_apos_vencimento || []).includes(dia);
                  return (
                    <button
                      key={dia}
                      type="button"
                      onClick={() => toggleDiaApos(dia)}
                      data-testid={`btn-dia-apos-${dia}`}
                      className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                        ativo
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-gray-700 border-gray-300 hover:border-emerald-300"
                      }`}
                    >
                      +{dia} dias
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Canais */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-500" />
              Canais de envio
            </CardTitle>
            <CardDescription>
              Escolha um ou mais. Os canais automáticos exigem credenciais.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {CANAIS_INFO.map((canal) => {
              const ativo = (config.canais || []).includes(canal.id);
              const isCredCanal = canal.automatico;
              const showThisCred = ativo && isCredCanal && showCreds[canal.id];
              return (
                <div key={canal.id} className="space-y-2">
                  <button
                    type="button"
                    onClick={() => canal.ativo && toggleCanal(canal.id)}
                    data-testid={`btn-canal-${canal.id}`}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all flex items-start gap-3 ${
                      ativo
                        ? "border-emerald-500 bg-emerald-50"
                        : "border-gray-200 hover:border-emerald-300 bg-white"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full shrink-0 mt-0.5 flex items-center justify-center ${
                        ativo ? "bg-emerald-600" : "bg-gray-200"
                      }`}
                    >
                      {ativo && <CheckCircle2 className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{canal.nome}</span>
                        <Badge
                          className={canal.fase === 2 ? "bg-emerald-600" : "bg-blue-600"}
                        >
                          {canal.badge}
                        </Badge>
                        <span className="text-xs text-gray-500">{canal.custo}</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{canal.descricao}</p>
                    </div>
                    {isCredCanal && ativo && (
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowCreds((s) => ({ ...s, [canal.id]: !s[canal.id] }));
                        }}
                        className="text-emerald-600 inline-flex items-center cursor-pointer p-1"
                        data-testid={`btn-toggle-creds-${canal.id}`}
                      >
                        {showCreds[canal.id] ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    )}
                  </button>

                  {showThisCred && canal.id === "whatsapp_zapi" && (
                    <div className="ml-8 p-3 bg-white border rounded-lg space-y-2">
                      <div>
                        <Label className="text-xs">Instance ID</Label>
                        <Input
                          value={config.zapi_instance_id || ""}
                          onChange={(e) => update("zapi_instance_id", e.target.value)}
                          placeholder="Ex: 3D5XXXX..."
                          data-testid="input-zapi-instance"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Token</Label>
                        <Input
                          type="password"
                          value={config.zapi_token || ""}
                          onChange={(e) => update("zapi_token", e.target.value)}
                          placeholder="Token da instância"
                          data-testid="input-zapi-token"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Client-Token (opcional)</Label>
                        <Input
                          type="password"
                          value={config.zapi_client_token || ""}
                          onChange={(e) => update("zapi_client_token", e.target.value)}
                          placeholder="Token de segurança da conta"
                          data-testid="input-zapi-client-token"
                        />
                      </div>
                      <div className="flex gap-2 items-end">
                        <div className="flex-1">
                          <Label className="text-xs">Telefone p/ enviar teste (opcional)</Label>
                          <Input
                            value={testPhone}
                            onChange={(e) => setTestPhone(e.target.value)}
                            placeholder="5511999999999"
                            data-testid="input-test-phone-zapi"
                          />
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => testConnection("zapi")}
                          disabled={testing.zapi}
                          data-testid="btn-test-zapi"
                        >
                          {testing.zapi ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Zap className="w-4 h-4" />
                          )}{" "}
                          Testar
                        </Button>
                      </div>
                    </div>
                  )}

                  {showThisCred && canal.id === "whatsapp_evolution" && (
                    <div className="ml-8 p-3 bg-white border rounded-lg space-y-2">
                      <div>
                        <Label className="text-xs">Base URL</Label>
                        <Input
                          value={config.evolution_base_url || ""}
                          onChange={(e) => update("evolution_base_url", e.target.value)}
                          placeholder="https://api.seuservidor.com"
                          data-testid="input-evolution-baseurl"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Instance Name</Label>
                        <Input
                          value={config.evolution_instance || ""}
                          onChange={(e) => update("evolution_instance", e.target.value)}
                          placeholder="ggee-escola"
                          data-testid="input-evolution-instance"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">API Key</Label>
                        <Input
                          type="password"
                          value={config.evolution_apikey || ""}
                          onChange={(e) => update("evolution_apikey", e.target.value)}
                          placeholder="API key da instância"
                          data-testid="input-evolution-apikey"
                        />
                      </div>
                      <div className="flex gap-2 items-end">
                        <div className="flex-1">
                          <Label className="text-xs">Telefone p/ enviar teste (opcional)</Label>
                          <Input
                            value={testPhone}
                            onChange={(e) => setTestPhone(e.target.value)}
                            placeholder="5511999999999"
                            data-testid="input-test-phone-evolution"
                          />
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => testConnection("evolution")}
                          disabled={testing.evolution}
                          data-testid="btn-test-evolution"
                        >
                          {testing.evolution ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Zap className="w-4 h-4" />
                          )}{" "}
                          Testar
                        </Button>
                      </div>
                    </div>
                  )}

                  {showThisCred && canal.id === "email_resend" && (
                    <div className="ml-8 p-3 bg-white border rounded-lg space-y-2">
                      <div>
                        <Label className="text-xs">Resend API Key</Label>
                        <Input
                          type="password"
                          value={config.resend_api_key || ""}
                          onChange={(e) => update("resend_api_key", e.target.value)}
                          placeholder="re_xxxxxxxxxxxxx"
                          data-testid="input-resend-key"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">
                          Sender Email{" "}
                          <span className="text-gray-500">
                            (use onboarding@resend.dev para começar)
                          </span>
                        </Label>
                        <Input
                          value={config.resend_sender_email || ""}
                          onChange={(e) => update("resend_sender_email", e.target.value)}
                          placeholder="onboarding@resend.dev"
                          data-testid="input-resend-sender"
                        />
                      </div>
                      <div className="flex gap-2 items-end">
                        <div className="flex-1">
                          <Label className="text-xs">E-mail p/ enviar teste (opcional)</Label>
                          <Input
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                            placeholder="seuemail@gmail.com"
                            data-testid="input-test-email-resend"
                          />
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => testConnection("resend")}
                          disabled={testing.resend}
                          data-testid="btn-test-resend"
                        >
                          {testing.resend ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Zap className="w-4 h-4" />
                          )}{" "}
                          Testar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Mensagem WhatsApp */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-emerald-500" />
              Mensagem WhatsApp
            </CardTitle>
            <CardDescription>
              Variáveis:{" "}
              <code className="text-xs bg-gray-100 px-1 rounded">{"{responsavel}"}</code>{" "}
              <code className="text-xs bg-gray-100 px-1 rounded">{"{aluno}"}</code>{" "}
              <code className="text-xs bg-gray-100 px-1 rounded">{"{valor}"}</code>{" "}
              <code className="text-xs bg-gray-100 px-1 rounded">{"{dia_vencimento}"}</code>{" "}
              <code className="text-xs bg-gray-100 px-1 rounded">{"{nome_escola}"}</code>{" "}
              <code className="text-xs bg-gray-100 px-1 rounded">{"{link}"}</code>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={config.mensagem_whatsapp_template || ""}
              onChange={(e) => update("mensagem_whatsapp_template", e.target.value)}
              rows={8}
              className="font-mono text-sm"
              data-testid="textarea-msg-whatsapp"
            />
          </CardContent>
        </Card>

        {/* Mensagem E-mail */}
        {(config.canais || []).includes("email_resend") && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-500" />
                E-mail (Resend)
              </CardTitle>
              <CardDescription>
                Assunto e HTML do e-mail enviado aos responsáveis.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">Assunto</Label>
                <Input
                  value={config.mensagem_email_subject || ""}
                  onChange={(e) => update("mensagem_email_subject", e.target.value)}
                  data-testid="input-email-subject"
                />
              </div>
              <div>
                <Label className="text-xs">HTML</Label>
                <Textarea
                  rows={10}
                  className="font-mono text-xs"
                  value={config.mensagem_email_template_html || ""}
                  onChange={(e) =>
                    update("mensagem_email_template_html", e.target.value)
                  }
                  data-testid="textarea-email-html"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Histórico */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-gray-500" />
              Estatísticas de envio
            </CardTitle>
          </CardHeader>
          <CardContent>
            {historico ? (
              <div className="grid grid-cols-3 gap-2 sm:gap-3 text-center">
                <div className="p-2 sm:p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <div className="text-xl sm:text-2xl font-bold text-emerald-700" data-testid="stat-enviados-24h">
                    {historico.enviados_24h}
                  </div>
                  <div className="text-[10px] sm:text-xs text-gray-600">Enviados 24h</div>
                </div>
                <div className="p-2 sm:p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="text-xl sm:text-2xl font-bold text-blue-700" data-testid="stat-enviados-30d">
                    {historico.enviados_30d}
                  </div>
                  <div className="text-[10px] sm:text-xs text-gray-600">Enviados 30d</div>
                </div>
                <div className="p-2 sm:p-3 rounded-lg bg-red-50 border border-red-200">
                  <div className="text-xl sm:text-2xl font-bold text-red-700" data-testid="stat-falhas-30d">
                    {historico.falhas_30d}
                  </div>
                  <div className="text-[10px] sm:text-xs text-gray-600">Falhas 30d</div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">Sem histórico ainda.</div>
            )}
          </CardContent>
        </Card>

        {/* Baixa automática */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  Baixa automática (Webhook MP)
                </CardTitle>
                <CardDescription>
                  Mensalidades são marcadas como pagas automaticamente após
                  pagamento via Mercado Pago.
                </CardDescription>
              </div>
              <Switch
                checked={config.auto_baixa_mp}
                onCheckedChange={(v) => update("auto_baixa_mp", v)}
                data-testid="switch-auto-baixa"
              />
            </div>
          </CardHeader>
          <CardContent>
            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <AlertDescription className="text-sm text-amber-900">
                <strong>Configure 1x no Mercado Pago:</strong> Painel MP →
                Webhooks → Adicionar URL{" "}
                <code className="bg-white px-1.5 py-0.5 rounded text-xs">
                  {window.location.origin}/api/pagamentos/webhook
                </code>{" "}
                → Evento <em>Pagamentos</em>.{" "}
                <a
                  href="/ajuda/pagamentos"
                  className="underline font-semibold"
                >
                  Ver tutorial
                </a>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Save */}
        <div className="flex justify-end gap-2 sticky bottom-4">
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            className="bg-emerald-600 hover:bg-emerald-700 shadow-lg"
            data-testid="btn-salvar-cobranca-config"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Salvar configuração
          </Button>
        </div>
      </div>
    </Layout>
  );
}
